import { EyeFilled, HeartFilled, HeartOutlined, WarningOutlined } from '@ant-design/icons';
import { useAuth0 } from '@auth0/auth0-react';
import {
  useBoatExistsToCompare,
  useComparableBoats,
  useToggleCompare,
} from '@components/CompareBoats/hooks/useCompare';
import { useAddToFavoriteBoat, useRemoveFavoriteBoat, useUserFavorites } from '@hooks';
import styles from '@styles/SingleBoat/jumbotron.module.scss';
import { getBoatName } from '@utils/getBoatName';
import { Button, Checkbox, Divider, message } from 'antd';
import cn from 'classnames';
import Link from 'next/link';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useScrollHandler } from 'services';
import { Boat } from 'services/graphql/cms';
import { BreadCrumb } from '../BreadCrumb';
import CompareBoats from './Compare';

type Props = {
  pageName: string;
  breadCrumb: number[];
  setJumbotronHeight: (height: number) => void;
  boat: Boat;
};

// TODO: to fix boat favorite count '
const favoritesCount = null;

const Jumbotron: React.FC<Props> = ({ pageName, breadCrumb, setJumbotronHeight, boat }) => {
  const [isHydrates, setIsHydrated] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  const boatId = Number(boat.documentId);

  const comparableBoats = useComparableBoats();
  const boatExistsToCompare = useBoatExistsToCompare();
  const toggleCompare = useToggleCompare();

  const { isAuthenticated } = useAuth0();

  const warningFavorite = () => {
    messageApi.open({
      type: 'warning',
      content: ' You must be logged in to add to favourite boat! ',
      duration: 2,
    });
  };

  const { isLoadingFavorite, isFavourited, reFetchFavorite } = useUserFavorites(boatId);
  const { isLoadingRemove, removeFavoriteFn } = useRemoveFavoriteBoat();
  const { isLoadingAdd, addToFavoriteFn } = useAddToFavoriteBoat();

  const favoriteBoat = useCallback(
    (id: number) => {
      if (!isFavourited) {
        addToFavoriteFn(id, {
          onSettled: () => {
            reFetchFavorite();
          },
        });
      } else {
        removeFavoriteFn(id, {
          onSettled: () => {
            reFetchFavorite();
          },
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isFavourited, addToFavoriteFn, removeFavoriteFn, reFetchFavorite],
  );

  const isLoading = isLoadingFavorite || isLoadingRemove || isLoadingAdd;

  const FavoriteButton = useMemo(
    () => (
      <Button
        icon={isFavourited ? <HeartFilled /> : <HeartOutlined />}
        loading={isLoading}
        onClick={() => favoriteBoat(boatId)}
        title={isFavourited ? 'Unfavourited boat' : 'Favorite Boat'}
        className={`${styles.saveAction} ${isFavourited ? styles.boatFav : ''}`}
      />
    ),
    [isFavourited, isLoading, boatId, favoriteBoat],
  );

  // const compareConfig = useMemo<CompareActionConfig>(
  //   () => ({
  //     isAuthenticated,
  //     messageInstance,
  //     notificationInstance,
  //     router,
  //   }),
  //   [isAuthenticated, messageInstance, notificationInstance, router],
  // );

  const { views_count: viewsCount } = boat;

  const boatTitle = useMemo(() => getBoatName(boat), [boat]);

  const boatIsToCompare = boatExistsToCompare(boat.documentId) || false;

  const { stickyHeader } = useScrollHandler();

  const [stickyJumbotron, setStickyJumbotron] = useState<boolean>(false);

  const container = useRef<HTMLElement>(null);

  useEffect(() => {
    const handleScroll = (): void => {
      if (!container.current) {
        return;
      }

      setJumbotronHeight(container.current.offsetHeight + 64);

      if (window.pageYOffset > container.current.offsetHeight + 64) {
        setStickyJumbotron(true);
      } else {
        setStickyJumbotron(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setJumbotronHeight]);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  return (
    <section
      className={cn(
        styles.container,
        { [styles.containerSticky]: stickyJumbotron },
        { [styles.containerStickyHeader]: stickyHeader },
      )}
      ref={container}
    >
      <div className={styles.breadcrumbContainer}>
        <BreadCrumb pageName={pageName} breadCrumb={breadCrumb} />
        {isHydrates && !!comparableBoats?.length && <CompareBoats />}
      </div>

      <div className={styles.containerContent}>
        <h1 className={styles.boatTitle}>{boatTitle}</h1>

        <div className={styles.boatActions}>
          {isHydrates && (
            <Checkbox
              onChange={() => toggleCompare({ documentId: boat.documentId, attributes: boat })}
              className={styles.compareAction}
              checked={boatIsToCompare}
            >
              Add to compare
            </Checkbox>
          )}
          <Divider type="vertical" className={styles.divider} />

          <div>
            {/* <Button type="ghost" className={styles.shareAction}>
              <ShareAltOutlined />
            </Button> */}

            {isAuthenticated ? (
              FavoriteButton
            ) : (
              <Button onClick={warningFavorite} title="Favorite Boat" icon={<HeartOutlined />} />
            )}
          </div>
        </div>

        <div className={styles.boatStatistics}>
          {viewsCount && (
            <p className={styles.boatViews}>
              <EyeFilled /> {viewsCount}
            </p>
          )}
          {favoritesCount && (
            <p className={styles.boatSaves}>
              <HeartFilled /> {favoritesCount}
            </p>
          )}
        </div>

        {!stickyJumbotron && (
          <Link
            href="https://support.marinedatacloud.com/portal/en/newticket?departmentId=446184000000145077"
            passHref
            target="_blank"
            rel="noopener noreferrer"
            className={styles.boatReport}
          >
            <WarningOutlined />
            <p>Report error</p>
          </Link>
        )}
      </div>
      {contextHolder}
    </section>
  );
};

export default Jumbotron;
